import React, { useState, useEffect, useRef } from 'react';

import { Form, Input, InputNumber, Select, DatePicker, Button, Row, Col, message, Card, Segmented, Modal, Space } from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { toDayjsOrNull } from '@utils/dateTime';

import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import ProductItemCard from '@components/shared/items/ProductItemCard';
import InventoryBatchTags from '@components/shared/items/InventoryBatchTags';
import InventorySelectorModal from '@components/shared/selectors/InventorySelectorModal';
import useSmartBack from '@hooks/shared/useSmartBack';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import { stockTransferOrderService } from '@services/operator/stockTransferOrderService';
import { warehouseService } from '@services/operator/warehouseService';
import { handleApiError } from '@utils/apiErrorHandler';

const MODE_PRECISE = 'precise';
const MODE_INPUT = 'input';

type ItemMode = typeof MODE_PRECISE | typeof MODE_INPUT;

interface WarehouseLike {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface STOItemRecord {
  id?: string;
  source_inventory_id?: string;
  product_id?: string;
  product?: { id?: string; sku?: string; name?: string } | null;
  inventory_type_id?: string;
  inventory_type?: { id?: string; name?: string } | null;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown>;
  quantity?: number;
  [key: string]: unknown;
}

interface STORecord {
  id?: string;
  name?: string;
  source_warehouse?: { id?: string } | null;
  destination_warehouse?: { id?: string } | null;
  eta?: string | null;
  remarks?: string | null;
  items?: STOItemRecord[];
  merchant?: { id?: string } | null;
  [key: string]: unknown;
}

interface InventoryTypeOption {
  id: string;
  code?: string;
  name: string;
  [key: string]: unknown;
}

interface StockTransferOrderFormProps {
  merchantId?: string;
  initialData?: STORecord;
  // Reference data flows in from the wrapper, scoped to the *record's* merchant
  // (see useRecordMerchantSync). Avoid reading useOperatorData() here so a
  // header-merchant switch can't poison the dropdowns mid-edit.
  inventoryTypes?: InventoryTypeOption[];
  // True while wrapper is still aligning OperatorDataContext.currentMerchant to
  // the loaded record. We must NOT render dropdowns or fire dependent queries
  // until this clears, otherwise antd Select falls back to raw UUIDs.
  merchantDataLoading?: boolean;
}

const StockTransferOrderForm: React.FC<StockTransferOrderFormProps> = ({
  merchantId,
  initialData,
  inventoryTypes = [],
  merchantDataLoading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const [form] = Form.useForm();
  const productService = useOperatorProductService({ merchantId });

  const [submitting, setSubmitting] = useState<boolean>(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState<boolean>(false);
  // Existing-item "更換" 按下時記錄正在編輯的 fieldName + 開啟共用 modal。
  // formListAddRef 在 Form.List render-prop 內被設成 add()，讓 modal confirm 後能展開新卡。
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const formListAddRef = useRef<((item: any) => void) | null>(null);
  const [suggesting, setSuggesting] = useState<boolean>(false);
  const [warehouses, setWarehouses] = useState<WarehouseLike[]>([]);
  const [mode, setMode] = useState<ItemMode>(() => {
    if (initialData) {
      const items = initialData.items || [];
      if (items.some((i) => i.source_inventory_id)) return MODE_PRECISE;
      if (items.some((i) => i.batch || i.expiration_date)) return MODE_INPUT;
      return MODE_INPUT;
    }
    return (localStorage.getItem('sto_item_mode') as ItemMode) || MODE_PRECISE;
  });

  const isEditMode = !!initialData;

  // inventoryTypes prop already scoped to the record's merchant by the wrapper;
  // no need to re-derive from useOperatorData() here.
  const effectiveInventoryTypes = inventoryTypes;

  // Load warehouse list
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await warehouseService.fetchList({ page_size: 100 });
        if (res.success) {
          setWarehouses(res.data || []);
        }
      } catch (e) {
        console.error('Failed to fetch warehouses', e);
      }
    };
    fetchWarehouses();
  }, []);

  // Populate form when initialData is provided (edit mode)
  useEffect(() => {
    if (initialData && warehouses.length > 0) {
      const formValues = {
        source_warehouse_id: initialData.source_warehouse?.id,
        destination_warehouse_id: initialData.destination_warehouse?.id,
        eta: toDayjsOrNull(initialData.eta),
        remarks: initialData.remarks || '',
        items: (initialData.items || []).map((item) => ({
          _existing: true,
          inventory_id: item.source_inventory_id,
          // Backend nests product id under `product.id`; older code read item.product_id (top-level)
          // which was undefined, breaking the modal's productId lookup for the 更換 flow.
          product_id: item.product?.id || item.product_id,
          product_sku: item.product?.sku || '',
          product_name: item.product?.name || '',
          inventory_type_name: item.inventory_type?.name || '',
          inventory_type_id: item.inventory_type_id,
          batch: item.batch || '',
          // BE returns ISO-string date; submit path (.format below) requires Dayjs.
          expiration_date: toDayjsOrNull(item.expiration_date),
          manufacturing_date: toDayjsOrNull(item.manufacturing_date),
          custom_attributes: item.custom_attributes || {},
          quantity: item.quantity,
        })),
      };
      form.setFieldsValue(formValues);
    }
  }, [initialData, warehouses, mode, form]);

  const sourceWarehouseId = Form.useWatch('source_warehouse_id', form);

  // When source warehouse changes in create mode, clear destination and items
  const handleSourceWarehouseChange = (_warehouseId: string) => {
    form.setFieldValue('destination_warehouse_id', undefined);
    // Clear items when source warehouse changes
    form.setFieldValue('items', []);
  };

  // Multi-select expansion: first pick replaces current card in place, rest append.
  // Applies whether the current card already had an inventory_id or not — the
  // entry point is always the "變更/選擇庫存" button, whose semantic is
  // "set this row's inventory", never "add more rows alongside". In edit mode
  // the spread preserves currentItem.id, so a replaced existing item submits
  // as an UPDATE (changed inventory_id), not DELETE+CREATE.
  const expandInventoriesIntoItems = (
    currentFieldIndex: number,
    picks: Array<{ inventory: any; allocatedQuantity: number }>,
    add: (item: any) => void,
  ) => {
    if (!picks || picks.length === 0) return;

    const currentItem: any = form.getFieldValue(['items', currentFieldIndex]) || {};

    const buildItem = (pick: { inventory: any; allocatedQuantity: number }) => {
      const inv = pick.inventory;
      return {
        product_id: currentItem.product_id,
        product_sku: currentItem.product_sku,
        product_name: currentItem.product_name,
        product_validity_days: currentItem.product_validity_days,
        manufacturing_date_required: currentItem.manufacturing_date_required,
        batch_management_required: currentItem.batch_management_required,
        inventory_id: inv.id,
        inventory_type_id: inv.inventory_type
          ? { value: inv.inventory_type.id, label: inv.inventory_type.name }
          : undefined,
        batch: inv.batch || null,
        // BE returns ISO-string date; submit path (.format below) requires Dayjs.
        expiration_date: toDayjsOrNull(inv.expiration_date),
        manufacturing_date: toDayjsOrNull(inv.manufacturing_date),
        custom_attributes: inv.custom_attributes || {},
        quantity: pick.allocatedQuantity,
        _source_inventory: inv,
      };
    };

    const [first, ...rest] = picks;
    form.setFieldValue(['items', currentFieldIndex], {
      ...currentItem,
      ...buildItem(first),
    });
    rest.forEach((p) => add(buildItem(p)));
  };

  const computeExcludeIdsExcept = (currentFieldIndex: number): string[] => {
    const items: any[] = form.getFieldValue('items') || [];
    return items
      .map((it, idx) =>
        idx === currentFieldIndex ? null : it?.inventory_id?.value || it?.inventory_id,
      )
      .filter(Boolean) as string[];
  };

  // 切模式不清空品項（3-zone layout 確保 form state 不丟）
  const handleModeChange = (newMode: ItemMode) => {
    setMode(newMode);
    localStorage.setItem('sto_item_mode', newMode);
  };

  // 建議單號：呼叫後端產生 STO-YYYYMMDD-NNNN 格式
  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await stockTransferOrderService.suggestName(merchantId);
      if (res.success) {
        form.setFieldsValue({ barcode: res.data.name });
      } else {
        message.error(res.message || (t('common.error_label') as string));
      }
    } catch (e) {
      console.error(e);
      message.error(t('common.error_label') as string);
    } finally {
      setSuggesting(false);
    }
  };

  // 將 form values 轉成 API payload
  const buildPayload = (values: any) => {
    const items: any[] = values.items || [];
    return {
      merchant_id: merchantId,
      source_warehouse_id: values.source_warehouse_id,
      destination_warehouse_id: values.destination_warehouse_id,
      barcode: values.barcode,
      eta: values.eta ? (values.eta as Dayjs).format('YYYY-MM-DD') : null,
      remarks: values.remarks,
      items: items.map((item) => {
        if (item._existing) {
          // Existing item — IDs are plain strings
          return {
            inventory_id: item.inventory_id,
            quantity: item.quantity,
          };
        }
        if (mode === MODE_PRECISE) {
          return {
            inventory_id: item.inventory_id?.value || item.inventory_id,
            quantity: item.quantity,
          };
        }
        // Input mode — ProductItemCard 寫入後 product_id 是純字串，
        // edit 模式 seed 進來時可能是 {value, label}，兩種都要處理
        return {
          product_id: item.product_id?.value || item.product_id,
          inventory_type_id: item.inventory_type_id?.value || item.inventory_type_id,
          quantity: item.quantity,
          batch: item.batch || null,
          expiration_date: item.expiration_date ? (item.expiration_date as Dayjs).format('YYYY-MM-DD') : null,
        };
      }),
    };
  };

  // 核心提交邏輯：組 payload 後呼叫 create/update
  const submitValues = async (values: any) => {
    const payload = buildPayload(values);
    if (isEditMode) {
      const id = initialData?.id;
      if (!id) throw new Error('StockTransferOrderForm: edit mode requires initialData.id');
      return await stockTransferOrderService.update(id, payload);
    }
    return await stockTransferOrderService.create(payload);
  };

  // 主要提交：成功後導向該筆記錄的 show 頁
  const onFinish = async (values: any) => {
    const items: any[] = values.items || [];
    if (items.length === 0) {
      message.error(t('operator.stock_transfer_orders.form.items_required') as string);
      return;
    }

    setSubmitting(true);
    try {
      const res = await submitValues(values);

      if (res.success) {
        message.success(t(isEditMode ? 'common.update_success' : 'common.create_success') as string);
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        const recordId = res.data?.id || initialData?.id;
        navigate(`/stock_transfer_orders/${recordId}`, { replace: true });
      } else {
        handleApiError(res, {
          t,
          fallbackMessage: t(isEditMode ? 'common.error.update_failed' : 'common.error.create_failed') as string,
        });
      }
    } catch (e) {
      console.error(e);
      // apiService 對非 422 的失敗 throw Error，code/interpolations 帶在 e 上；
      // 翻成跟 res.success=false 路徑一樣的友善訊息
      const fallback = t(isEditMode ? 'common.error.update_failed' : 'common.error.create_failed') as string;
      const err = e as { errorCode?: string; interpolations?: Record<string, unknown> };
      if (err?.errorCode) {
        handleApiError({ message: err.errorCode, interpolations: err.interpolations }, { t, fallbackMessage: fallback });
      } else {
        message.error(fallback);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      const items: any[] = values.items || [];
      if (items.length === 0) {
        message.error(t('operator.stock_transfer_orders.form.items_required') as string);
        return;
      }

      setSavingAndAdding(true);
      try {
        const res = await submitValues(values);

        if (res.success) {
          message.success(t('common.create_success') as string);
          // 重置 antd Form 欄位（包含 Form.List items）
          form.resetFields();
        } else {
          handleApiError(res, { t, fallbackMessage: t('common.error.create_failed') as string });
        }
      } catch (e) {
        console.error(e);
        const fallback = t('common.error.create_failed') as string;
        const err = e as { errorCode?: string; interpolations?: Record<string, unknown> };
        if (err?.errorCode) {
          handleApiError({ message: err.errorCode, interpolations: err.interpolations }, { t, fallbackMessage: fallback });
        } else {
          message.error(fallback);
        }
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // 在 wrapper 還沒把 currentMerchant 對齊到 record 之前，先不要 render form：
  // 否則下拉清單會用前一個 merchant 的 reference data，把 record 的 ids
  // 渲染成 raw UUID，使用者一存就把 B 的 id 寫進 A 的單據。
  if (merchantDataLoading) {
    return null;
  }

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} scrollToFirstError>
      {/* 調撥單號 (僅新建時顯示，附建議按鈕) */}
      {!isEditMode && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('models.stock_transfer_order.barcode')}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="barcode" noStyle>
                  <Input
                    data-testid="sto-barcode-input"
                    placeholder={t('shared.stock_transfer_order.barcode_placeholder')}
                  />
                </Form.Item>
                <Button
                  onClick={handleSuggestName}
                  loading={suggesting}
                  disabled={submitting || savingAndAdding}
                >
                  {t('shared.stock_transfer_order.suggest_name')}
                </Button>
              </Space.Compact>
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* Header fields */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="source_warehouse_id"
            label={t('models.stock_transfer_order.source_warehouse')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Select
              placeholder={t('common.placeholder.select')}
              onChange={handleSourceWarehouseChange}
              options={warehouses.map((w) => ({ label: w.name, value: w.id }))}
              showSearch
              optionFilterProp="label"
              disabled={isEditMode}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="destination_warehouse_id"
            label={t('models.stock_transfer_order.destination_warehouse')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Select
              placeholder={t('common.placeholder.select')}
              options={warehouses
                .filter((w) => w.id !== sourceWarehouseId)
                .map((w) => ({ label: w.name, value: w.id }))}
              showSearch
              optionFilterProp="label"
              disabled={isEditMode || !sourceWarehouseId}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="eta" label={t('models.stock_transfer_order.eta')}>
            <DatePicker
              style={{ width: '100%' }}
              placeholder={t('common.placeholder.select_date')}
              disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
            />
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item name="remarks" label={t('models.stock_transfer_order.remarks')}>
            <Input.TextArea rows={1} />
          </Form.Item>
        </Col>
      </Row>

      {/* Items section */}
      <Card style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontWeight: 600, fontSize: 16 }}>
              {t('operator.stock_transfer_orders.form.items_title')}
            </span>
            <Segmented
              value={mode}
              onChange={(v) => handleModeChange(v as ItemMode)}
              options={[
                { label: t('shared.orders.form.item_mode.input'), value: MODE_INPUT },
                { label: t('shared.orders.form.item_mode.precise'), value: MODE_PRECISE },
              ]}
            />
          </div>
        </div>

        <Form.List name="items">
          {(fields, { add, remove }) => {
            // Expose add() to the shared "更換" modal (rendered outside this render-prop scope).
            formListAddRef.current = add;
            return (
            <>
              {fields.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#999',
                  border: '1px dashed #d9d9d9',
                  borderRadius: '4px',
                }}>
                  {sourceWarehouseId
                    ? t('operator.stock_transfer_orders.form.items_required')
                    : t('operator.stock_transfer_orders.form.select_source_warehouse_first')
                  }
                </div>
              ) : (
                fields.map(({ key, name: fieldName, ...restField }) => {
                  const itemData: any = form.getFieldValue(['items', fieldName]) || {};

                  // Existing items: readonly display (product + inventory tags) with editable quantity.
                  // Visual layout mirrors ProductItemCard's precise-mode "selected" tags so Edit-mode
                  // cards look consistent with newly added cards.
                  if (itemData._existing) {
                    return (
                      <Card key={key} className="product-item-card" style={{ marginBottom: 16 }} styles={{ body: { padding: '16px' } }}>
                        <Row gutter={16} align="top">
                          <Col span={22}>
                            <Row gutter={16}>
                              <Col span={10}>
                                <Form.Item label={t('shared.inbounds.form.product')}>
                                  <Input value={`${itemData.product_sku} - ${itemData.product_name}`} disabled />
                                </Form.Item>
                              </Col>
                              <Col span={10}>
                                <Form.Item label={t('shared.orders.form.item_mode.select_inventory', { defaultValue: '選擇庫存批次' })}>
                                  <div
                                    onClick={() => setEditingFieldIndex(fieldName)}
                                    style={{
                                      border: '1px solid #d9d9d9',
                                      borderRadius: 6,
                                      padding: '4px 11px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      minHeight: 32,
                                      cursor: 'pointer',
                                      transition: 'border-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1677ff'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d9d9d9'; }}
                                  >
                                    <InventoryBatchTags
                                      inventoryTypeName={itemData.inventory_type_name}
                                      batch={itemData.batch}
                                      expirationDate={itemData.expiration_date}
                                      manufacturingDate={itemData.manufacturing_date}
                                      customAttributes={itemData.custom_attributes}
                                    />
                                    <Button type="link" size="small" icon={<SearchOutlined />} style={{ padding: 0 }}>
                                      {t('common.button.change', { defaultValue: '變更' })}
                                    </Button>
                                  </div>
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Form.Item
                                  {...restField}
                                  name={[fieldName, 'quantity']}
                                  label={t('models.inventory.quantity')}
                                  rules={[{ required: true, message: t('common.validation.required') }]}
                                >
                                  <InputNumber min={1} style={{ width: '100%' }} />
                                </Form.Item>
                              </Col>
                            </Row>
                            {/* Hidden fields for submission */}
                            <Form.Item name={[fieldName, '_existing']} noStyle hidden><Input /></Form.Item>
                            <Form.Item name={[fieldName, 'inventory_id']} noStyle hidden><Input /></Form.Item>
                            <Form.Item name={[fieldName, 'product_id']} noStyle hidden><Input /></Form.Item>
                          </Col>
                          <Col span={2} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(fieldName)}>
                              {t('common.button.delete')}
                            </Button>
                          </Col>
                        </Row>
                      </Card>
                    );
                  }

                  // New items: unified card with inventoryMode
                  return (
                    <ProductItemCard
                      key={key}
                      name={fieldName}
                      restField={restField}
                      form={form}
                      listName="items"
                      searchProductsService={productService}
                      inventoryTypes={effectiveInventoryTypes}
                      onRemove={remove}
                      role="operator"
                      inventoryMode={mode}
                      sourceWarehouseId={sourceWarehouseId}
                      alwaysShowBatchFields={mode === MODE_INPUT}
                      multiSelectInventory={mode === MODE_PRECISE}
                      onMultiSelectInventory={(picks) => expandInventoriesIntoItems(fieldName, picks, add)}
                      excludeInventoryIds={computeExcludeIdsExcept(fieldName)}
                    />
                  );
                })
              )}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
                style={{ marginTop: 16 }}
                disabled={!sourceWarehouseId}
              >
                {t('operator.stock_transfer_orders.form.add_item')}
              </Button>
            </>
            );
          }}
        </Form.List>
      </Card>

      {/* Shared "更換" modal for existing items. Single instance, rendered outside Form.List
          so its visibility/state lives at the form level. Uses the same multi-select FEFO
          experience as new cards; A2 rule (currentHasInventory=true → all picks append,
          original kept) applies. */}
      <InventorySelectorModal
        visible={editingFieldIndex !== null}
        onCancel={() => setEditingFieldIndex(null)}
        productId={
          editingFieldIndex !== null
            ? (() => {
                const v = form.getFieldValue(['items', editingFieldIndex, 'product_id']);
                return v?.value || v;
              })()
            : undefined
        }
        productName={
          editingFieldIndex !== null
            ? form.getFieldValue(['items', editingFieldIndex, 'product_name'])
            : undefined
        }
        merchantId={merchantId}
        warehouseId={sourceWarehouseId}
        multiSelect
        excludeIds={editingFieldIndex !== null ? computeExcludeIdsExcept(editingFieldIndex) : []}
        onConfirmMultiple={(picks: Array<{ inventory: any; allocatedQuantity: number }>) => {
          if (editingFieldIndex !== null && formListAddRef.current) {
            expandInventoriesIntoItems(editingFieldIndex, picks, formListAddRef.current);
          }
          setEditingFieldIndex(null);
        }}
      />

      <FloatingActionBarSpacer />
      <FloatingActionBar
        role="operator"
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={submitting}
        disabled={savingAndAdding}
        extraButtons={!isEditMode ? (
          <Button
            loading={savingAndAdding}
            disabled={submitting}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
      />
    </Form>
  );
};

export default StockTransferOrderForm;
